import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { fetchWalletSummary } from '../../redux/slices/walletSlice';
import { Wallet, TrendingUp, TrendingDown, RefreshCw, Plus, AlertCircle } from 'lucide-react';

interface BalanceWidgetProps {
  onFundClick?: () => void;
  className?: string;
}

const BalanceWidget: React.FC<BalanceWidgetProps> = ({ onFundClick, className = '' }) => {
  const dispatch = useDispatch();
  const { summary, loading, error } = useSelector(
    (state: RootState) => state.wallet
  );

  useEffect(() => {
    dispatch(fetchWalletSummary() as any);
  }, [dispatch]);

  const balance = summary?.balance || 0;
  const totalFunded = summary?.totalFunded || 0;
  const totalUsed = summary?.totalUsed || 0;
  const currency = summary?.currency || 'NGN';

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: currency === 'NGN' ? 'NGN' : 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const utilizationRate = totalFunded > 0 ? Math.round((totalUsed / totalFunded) * 100) : 0;

  if (loading && !summary) {
    return (
      <div className={`card p-6 ${className}`}>
        <div className="flex items-center justify-between mb-6">
          <div className="skeleton h-5 w-32 rounded" />
          <div className="skeleton h-8 w-8 rounded-lg" />
        </div>
        <div className="skeleton h-10 w-48 rounded mb-2" />
        <div className="skeleton h-4 w-24 rounded mb-6" />
        <div className="grid grid-cols-2 gap-4">
          <div className="skeleton h-16 rounded-lg" />
          <div className="skeleton h-16 rounded-lg" />
        </div>
      </div>
    );
  }

  if (error && !summary) {
    return (
      <div className={`card p-6 ${className}`}>
        <div className="flex items-center gap-3 text-fintech-error">
          <AlertCircle size={20} />
          <div>
            <p className="font-semibold text-sm">Failed to load balance</p>
            <p className="text-xs text-fintech-textMuted mt-0.5">Please refresh to try again</p>
          </div>
        </div>
        <button
          className="btn-secondary mt-4 text-xs"
          onClick={() => dispatch(fetchWalletSummary() as any)}
        >
          <RefreshCw size={13} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`card overflow-hidden ${className}`}>
      {/* Header gradient band */}
      <div className="bg-gradient-to-br from-fintech-primary to-blue-700 px-6 pt-5 pb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
              <Wallet size={16} className="text-white" />
            </div>
            <span className="text-blue-200 text-sm font-medium">Account Balance</span>
          </div>
          <button
            className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            onClick={() => dispatch(fetchWalletSummary() as any)}
            title="Refresh balance"
            aria-label="Refresh balance"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Main balance display */}
        <div>
          <p className="text-3xl sm:text-4xl font-bold text-white tracking-tight leading-none mb-1">
            {formatCurrency(balance)}
          </p>
          <p className="text-blue-300 text-xs">
            Available for payments
          </p>
        </div>
      </div>

      {/* Stats section */}
      <div className="px-6 py-4 -mt-4">
        <div className="grid grid-cols-2 gap-3">
          {/* Total Funded */}
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
            <div className="flex items-center gap-1.5 mb-2">
              <TrendingUp size={13} className="text-emerald-600" />
              <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Funded</span>
            </div>
            <p className="text-base font-bold text-emerald-800 leading-tight">
              {formatCurrency(totalFunded)}
            </p>
            <p className="text-xs text-emerald-600 mt-0.5">Total deposited</p>
          </div>

          {/* Total Used */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-1.5 mb-2">
              <TrendingDown size={13} className="text-slate-500" />
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Used</span>
            </div>
            <p className="text-base font-bold text-fintech-text leading-tight">
              {formatCurrency(totalUsed)}
            </p>
            <p className="text-xs text-fintech-textMuted mt-0.5">Total spent</p>
          </div>
        </div>

        {/* Utilization bar */}
        {totalFunded > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-fintech-textMuted">Utilization</span>
              <span className="text-xs font-semibold text-fintech-text">{utilizationRate}%</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  utilizationRate > 80
                    ? 'bg-fintech-error'
                    : utilizationRate > 60
                    ? 'bg-fintech-warning'
                    : 'bg-fintech-accent'
                }`}
                style={{ width: `${Math.min(utilizationRate, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Fund button */}
        <button
          className="btn-primary w-full justify-center mt-4"
          onClick={onFundClick}
        >
          <Plus size={16} />
          Fund Account
        </button>
      </div>
    </div>
  );
};

export default BalanceWidget;
